import { Link, useLocation, useNavigate } from 'react-router-dom'
import { RiMenuLine, RiCloseLine } from 'react-icons/ri'
import { PiListChecks, PiHouse, PiUser, PiSignIn, PiUsersThree } from 'react-icons/pi'
import { useCallback, useEffect, useRef, useState } from 'react'

import { ROUTES } from '@/constants/routes'
import { LOGO, PROJECT_NAME } from "@/constants/common"
import { useAuthStore } from '@/stores/useAuthStore'
import { useWorkerProfile } from '@/api/worker'
import { BalanceChip } from '@/components/worker/BalanceChip/BalanceChip'
import type { UserDetails } from '@/types/auth'
import { Button, Spinner } from '@/components/ui'

import styles from './Navbar.module.css'

export function Navbar() {
    const navigate = useNavigate()
    const location = useLocation()

    const isLogged = useAuthStore((s) => s.isLogged)
    const getUser = useAuthStore((s) => s.getUser)
    const { data: workerProfile } = useWorkerProfile()

    const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
    const [userDetails, setUserDetails] = useState<UserDetails | undefined>(undefined)
    const [profileLoading, setProfileLoading] = useState(false)
    const [scrolled, setScrolled] = useState(false)
    const lastScrollY = useRef(0)

    useEffect(() => {
        if (isLogged) {
            setProfileLoading(true)
            setTimeout(() => {
                const userData = getUser()
                setUserDetails(userData ?? undefined)
                setProfileLoading(false)
            }, 500)
        }
    }, [isLogged, getUser])

    const handleScroll = useCallback(() => {
        const currentScrollY = window.scrollY
        setScrolled(currentScrollY > 10)
        lastScrollY.current = currentScrollY
    }, [])

    useEffect(() => {
        window.addEventListener('scroll', handleScroll, { passive: true })
        return () => window.removeEventListener('scroll', handleScroll)
    }, [handleScroll])

    // Lock body scroll when mobile menu is open
    useEffect(() => {
        if (mobileMenuOpen) {
            document.body.style.overflow = 'hidden'
        } else {
            document.body.style.overflow = ''
        }
        return () => { document.body.style.overflow = '' }
    }, [mobileMenuOpen])

    const isActive = (path: string) => location.pathname.startsWith(path)

    const isApprovedWorker = workerProfile?.status === 'approved'
    const currentUser = getUser()
    const isAdmin = currentUser?.is_staff === true

    const navItems = [
        { path: ROUTES.home, icon: PiHouse, label: 'Inicio' },
        ...(isApprovedWorker ? [
            { path: ROUTES.assignments, icon: PiListChecks, label: 'Mis Trabajos' },
        ] : []),
        ...(isAdmin ? [
            { path: ROUTES.admin, icon: PiUsersThree, label: 'Usuarios' },
        ] : []),
    ]

    const toggleMobileMenu = () => {
        setMobileMenuOpen(prev => !prev)
    }

    const closeMobileMenu = () => {
        setMobileMenuOpen(false)
    }

    const navbarClasses = [
        styles.navbar,
        scrolled ? styles.scrolled : '',
    ].filter(Boolean).join(' ')

    return (
        <>
            <nav className={navbarClasses}>
                <div className={styles.inner}>
                    {/* Logo */}
                    <Link to={ROUTES.home} className={styles.logo}>
                        <img src={LOGO.src} alt={LOGO.alt} className={styles.logoImg} />
                        <span className={styles.logoText}>{PROJECT_NAME}</span>
                    </Link>

                    {/* Center nav links (desktop) */}
                    <div className={styles.navCenter}>
                        {navItems.map((item) => (
                            <Link
                                key={item.label}
                                to={item.path}
                                className={`${styles.navLink} ${isActive(item.path) ? styles.active : ''}`}
                            >
                                <item.icon className={styles.navIcon} />
                                <span>{item.label}</span>
                            </Link>
                        ))}
                    </div>

                    {/* Right side actions (desktop) */}
                    <div className={styles.actions}>
                        {isApprovedWorker && workerProfile && (
                            <BalanceChip amount={workerProfile.balance} />
                        )}
                        {profileLoading ? (
                            <div className={styles.avatarWrap}>
                                <Spinner className={styles.avatarSpinner} />
                            </div>
                        ) : isLogged ? (
                            <Link to={ROUTES.profile} className={styles.avatarLink}>
                                <div className={styles.avatar}>
                                    <span>{userDetails?.username?.charAt(0).toUpperCase() || 'U'}</span>
                                </div>
                            </Link>
                        ) : (
                            <Button
                                variant="primary"
                                size="sm"
                                className={styles.loginBtn}
                                onClick={() => navigate(ROUTES.login)}
                            >
                                <PiSignIn className={styles.loginIcon} />
                                Iniciar Sesion
                            </Button>
                        )}
                    </div>

                    {/* Mobile hamburger */}
                    <button
                        className={styles.hamburger}
                        onClick={toggleMobileMenu}
                        aria-label={mobileMenuOpen ? 'Cerrar menu' : 'Abrir menu'}
                    >
                        {mobileMenuOpen ? (
                            <RiCloseLine size={22} />
                        ) : (
                            <RiMenuLine size={22} />
                        )}
                    </button>
                </div>
            </nav>

            {/* Mobile overlay menu */}
            {mobileMenuOpen && (
                <div className={styles.overlay} onClick={closeMobileMenu} />
            )}
            <div className={`${styles.mobilePanel} ${mobileMenuOpen ? styles.mobileOpen : ''}`}>
                <div className={styles.mobilePanelHeader}>
                    <button className={styles.mobileClose} onClick={closeMobileMenu} aria-label="Cerrar menu">
                        <RiCloseLine size={22} />
                    </button>
                </div>

                <div className={styles.mobileNav}>
                    {navItems.map((item) => (
                        <Link
                            key={item.label}
                            to={item.path}
                            className={`${styles.mobileLink} ${isActive(item.path) ? styles.mobileLinkActive : ''}`}
                            onClick={closeMobileMenu}
                        >
                            <item.icon className={styles.mobileLinkIcon} />
                            <span>{item.label}</span>
                        </Link>
                    ))}

                    {isLogged && (
                        <Link
                            to={ROUTES.profile}
                            className={`${styles.mobileLink} ${isActive(ROUTES.profile) ? styles.mobileLinkActive : ''}`}
                            onClick={closeMobileMenu}
                        >
                            <PiUser className={styles.mobileLinkIcon} />
                            <span>Perfil</span>
                        </Link>
                    )}
                </div>

                <div className={styles.mobileFooter}>
                    {isApprovedWorker && workerProfile && (
                        <div className={styles.mobileBalance}>
                            <BalanceChip amount={workerProfile.balance} />
                        </div>
                    )}
                    {!isLogged && (
                        <Button
                            variant="primary"
                            size="md"
                            className={styles.mobileLoginBtn}
                            onClick={() => { navigate(ROUTES.login); closeMobileMenu() }}
                        >
                            <PiSignIn className={styles.loginIcon} />
                            Iniciar Sesion
                        </Button>
                    )}
                </div>
            </div>
        </>
    )
}
